import clsx from 'clsx';
import { useEffect, useMemo } from 'react';

const POOLS_LIST_HEADER_ID = 'pools-list-header';
const MAIN_NAVBAR_ID = 'main-navbar';

type PageNumberSectionProps = {
  currentPage: number;
  hasNext: boolean;
  hasPrevious: boolean;
  totalPages: number;
  handlePageChange: (page: number) => void;
};

export default function PageNumberSection({
  currentPage,
  hasPrevious,
  hasNext,
  totalPages,
  handlePageChange,
}: PageNumberSectionProps) {
  useEffect(() => {
    if (!currentPage) return;
    const target = document.getElementById(POOLS_LIST_HEADER_ID);
    const navbar = document.getElementById(MAIN_NAVBAR_ID);
    if (!target) return;
    const scrollMarginTop = navbar ? navbar.getBoundingClientRect().height : 0;
    target.style.scrollMarginTop = `${scrollMarginTop}px`;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [currentPage]);

  const pages = useMemo(() => {
    const pages: string[] = [];

    pages.push('1');

    if (currentPage - 1 > 3) {
      pages.push('...');
      pages.push(`${currentPage - 1}`);
    } else {
      for (let i = 2; i < currentPage; i++) {
        pages.push(`${i}`);
      }
    }
    if (currentPage !== 1 && currentPage !== totalPages) {
      pages.push(`${currentPage}`);
    }

    if (totalPages - currentPage > 3) {
      pages.push(`${currentPage + 1}`);
      pages.push('...');
    } else {
      for (let i = currentPage + 1; i < totalPages; i++) {
        pages.push(`${i}`);
      }
    }

    if (totalPages > 1) {
      pages.push(`${totalPages}`);
    }

    return pages;
  }, [currentPage, totalPages]);

  return (
    <div className="flex items-center justify-center gap-4 py-6">
      {hasPrevious && (
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          className="flex items-center justify-center hover:bg-gray-10 transition-colors cursor-pointer"
        >
          <img src="/arrow_right.svg" alt="Left arrow" width={20} height={20} className="rotate-180" />
        </button>
      )}

      {/*Display page number or ... if type is string */}
      {totalPages > 0 && (
        <div className="flex items-center gap-2 cursor-pointer">
          {pages.map((page) =>
            page !== '...' ? (
              <button
                key={page}
                onClick={() => handlePageChange(+page)}
                className={clsx(
                  'px-3 py-1 text-sm font-medium transition-colors cursor-pointer',
                  +page === currentPage
                    ? 'text-[#113D7B] underline decoration-[#113D7B]'
                    : 'text-black hover:text-[#113D7B]'
                )}
              >
                {page}
              </button>
            ) : (
              <span key={page} className="px-2 text-sm font-medium text-[#767676]">
                ...
              </span>
            )
          )}
        </div>
      )}

      {hasNext && (
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          className="flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
        >
          <img src="/arrow_right.svg" alt="Right arrow" width={20} height={20} />
        </button>
      )}
    </div>
  );
}
